import type {
  ValidationFormData as ApiValidationFormData,
  FormDangerousGoodsItem,
  FormDeclarationParameters,
  CargoTransportUnitDto,
  FormEmergencyContact,
  FormEmergencyContactMethod,
} from '@/api/types';
import { DocumentType } from '@/api/generated-types';
import segregationGroupsData from '@shared/segregation-groups.json';

export type ValidationFormData = ApiValidationFormData;

export const DEFAULT_CTU_REFERENCE = 'CTU-1';

export interface SegregationGroupEntry {
  readonly code: string;
  readonly name: string;
}

export const SEGREGATION_GROUPS: readonly SegregationGroupEntry[] =
  segregationGroupsData as readonly SegregationGroupEntry[];

export interface HazardSubclass {
  code: string;
  name: string;
}

export interface HazardClassGroup {
  name: string;
  subclasses: readonly HazardSubclass[];
}

export const HAZARD_CLASSES: Record<number, HazardClassGroup> = {
  1: {
    name: 'Explosives',
    subclasses: [
      { code: '1.1A', name: 'Explosives' },
      { code: '1.1B', name: 'Explosives' },
      { code: '1.1C', name: 'Explosives' },
      { code: '1.1D', name: 'Explosives' },
      { code: '1.1E', name: 'Explosives' },
      { code: '1.1F', name: 'Explosives' },
      { code: '1.1G', name: 'Explosives' },
      { code: '1.1J', name: 'Explosives' },
      { code: '1.1L', name: 'Explosives' },
      { code: '1.2B', name: 'Explosives' },
      { code: '1.2C', name: 'Explosives' },
      { code: '1.2D', name: 'Explosives' },
      { code: '1.2E', name: 'Explosives' },
      { code: '1.2F', name: 'Explosives' },
      { code: '1.2G', name: 'Explosives' },
      { code: '1.2H', name: 'Explosives' },
      { code: '1.2J', name: 'Explosives' },
      { code: '1.2K', name: 'Explosives' },
      { code: '1.2L', name: 'Explosives' },
      { code: '1.3C', name: 'Explosives' },
      { code: '1.3F', name: 'Explosives' },
      { code: '1.3G', name: 'Explosives' },
      { code: '1.3H', name: 'Explosives' },
      { code: '1.3J', name: 'Explosives' },
      { code: '1.3K', name: 'Explosives' },
      { code: '1.3L', name: 'Explosives' },
      { code: '1.4B', name: 'Explosives' },
      { code: '1.4C', name: 'Explosives' },
      { code: '1.4D', name: 'Explosives' },
      { code: '1.4E', name: 'Explosives' },
      { code: '1.4F', name: 'Explosives' },
      { code: '1.4G', name: 'Explosives' },
      { code: '1.4S', name: 'Explosives' },
      { code: '1.5D', name: 'Explosives' },
      { code: '1.6N', name: 'Explosives' },
    ],
  },
  2: {
    name: 'Gases',
    subclasses: [
      { code: '2.1', name: 'Flammable gases' },
      { code: '2.2', name: 'Non-flammable, non-toxic gases' },
      { code: '2.3', name: 'Toxic gases' },
    ],
  },
  3: { name: 'Flammable liquids', subclasses: [] },
  4: {
    name: 'Flammable solids, etc.',
    subclasses: [
      { code: '4.1', name: 'Flammable solids, self-reactives and solid desensitized explosives' },
      { code: '4.2', name: 'Substances liable to spontaneous combustion' },
      { code: '4.3', name: 'Substances which, in contact with water, emit flammable gases' },
    ],
  },
  5: {
    name: 'Oxidizing substances and organic peroxides',
    subclasses: [
      { code: '5.1', name: 'Oxidizing substances' },
      { code: '5.2', name: 'Organic peroxides' },
    ],
  },
  6: {
    name: 'Toxic and infectious substances',
    subclasses: [
      { code: '6.1', name: 'Toxic substances' },
      { code: '6.2', name: 'Infectious substances' },
    ],
  },
  7: { name: 'Radioactive material', subclasses: [] },
  8: { name: 'Corrosive substances', subclasses: [] },
  9: { name: 'Miscellaneous', subclasses: [] },
};

// Flat list for lookups/validation — keeps backward compat with existing consumers
export const HAZARD_CLASS_OPTIONS = Object.entries(HAZARD_CLASSES).flatMap(([classNum, hc]) =>
  hc.subclasses.length > 0
    ? hc.subclasses.map((sc) => ({ code: sc.code, name: sc.name }))
    : [{ code: classNum, name: hc.name }]
);

export const DEFAULT_DECLARATION_PARAMETERS: FormDeclarationParameters = {
  isCoolant: false,
  isConditioner: false,
  isHot: false,
  isMolten: false,
  isStabilized: false,
  isMarinePollutant: false,
  isWaste: false,
  isEmptyUncleaned: false,
  isLimitedQuantity: false,
  isExceptedQuantity: false,
  isSalvagePackaging: false,
  isSalvagePressureReceptacle: false,
  isMixture: false,
  isSolution: false,
  isSample: false,
  isFoodstuffs: false,
  isStabilizedNotByTemperatureControl: null,
  isReportableQuantity: false,
  isPoisonInhalationHazard: null,
  isWettedOrDesensitized: false,
};

export const createEmptyContactMethod = (): FormEmergencyContactMethod => ({
  number: '',
  email: '',
  description: '',
});

export const createEmptyEmergencyContact = (): FormEmergencyContact => ({
  name: '',
  methods: [createEmptyContactMethod()],
  contract: '',
  available247: false,
});

// Default values for new dangerous goods items
export const createEmptyDangerousGoodsItem = (ctuId: string): FormDangerousGoodsItem => ({
  cargoId: crypto.randomUUID(),
  unNumber: '',
  properShippingName: '',
  technicalName: '',
  hazardClass: '',
  subsidiaryHazards: [],
  packingGroup: '',
  quantity: '',
  netWeight: undefined,
  grossWeight: undefined,
  netExplosiveContent: undefined,
  flashpoint: undefined,
  declarationParameters: { ...DEFAULT_DECLARATION_PARAMETERS },
  emsNumber: '',
  outerPackaging: { code: '', description: '', count: undefined, type: '' },
  innerPackaging: undefined,
  emergencyContactName: '',
  emergencyContactPhone: '',
  emergencyContact247: false,
  additionalSegregationGroups: [],
  ctuId,
});

export const createEmptyCtu = (ctuReference: string): CargoTransportUnitDto => {
  const ctuId = crypto.randomUUID();
  return {
    ctuId,
    ctuReference,
    unitType: 2, // ClosedUnit
    containerInformation: {
      isoCode: '',
      grossWeight: undefined,
      tareMassKg: undefined,
      volumeInM3: undefined,
      containerNumber: '',
      sealNumber: '',
    },
  };
};

// Generate next human-readable CTU reference (display label, not ID)
export const generateNextCtuReference = (existingCtus: CargoTransportUnitDto[]): string => {
  const existingRefs = existingCtus.map((ctu) => ctu.ctuReference ?? '');
  let counter = 1;
  while (existingRefs.includes(`CTU-${counter}`)) {
    counter++;
  }
  return `CTU-${counter}`;
};

// Default form data
export const createDefaultFormData = (): ValidationFormData => {
  const defaultCtu = createEmptyCtu(DEFAULT_CTU_REFERENCE);
  return {
    datasetName: '',
    description: '',
    tags: '',
    documentType: DocumentType.Dgd,
    bookingNumber: '',
    transportDocumentNumber: '',
    freightForwarderReference: '',
    customsReference: '',
    exportersReference: '',
    shipperReference: '',
    documentReference: '',
    shipper: { name: '', address: '', contact: '' },
    consignee: { name: '', address: '', contact: '' },
    freightForwarder: { name: '', address: '', contact: '' },
    carrier: { name: '', address: '', contact: '' },
    vesselName: '',
    voyageNumber: '',
    portOfLoading: '',
    portOfDischarge: '',
    placeOfDelivery: '',
    dangerousGoods: [createEmptyDangerousGoodsItem(defaultCtu.ctuId!)],
    cargoTransportUnits: [defaultCtu],
    specialInstructions: '',
    dgd: {
      containerPackingCertificate: {
        signed: false,
        date: '',
        place: '',
        signatoryName: '',
        signatoryPosition: '',
        company: '',
      },
      emergencyContacts: [createEmptyEmergencyContact()],
      declaration: {
        signed: false,
        date: '',
        place: '',
        signatoryName: '',
        signatoryPosition: '',
        company: '',
      },
    },
  };
};
